import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { AUTO_CALCULATE_FLIGHTS } from "./editCommand";
import { getIsoDay, mergeCalculatedGroundTimeChanges, normalizeTimeValue, valuesEqual } from "./flightTimingUtils";
import { buildAircraftWorkingFlightsSnapshot } from "./workingFlightsSnapshot";
import { scheduleDraftDataService } from "../services/scheduleDraftDataService";

// index.js/App.jsx do not initialize dayjs plugins yet; keep module-level UTC registration.
dayjs.extend(utc);

export const BATCH_AUTO_CALCULATE_OPTIONS = {
    consider_min_ground_time: true,
    consider_curfew: true,
    consider_transport_demand: true,
    consider_aircraft_availability: true,
    minimum_impact_mode: true,
};

export function getAutoCalculateAffectedFlightId(affectedFlight) {
    return affectedFlight?.flight_id ?? affectedFlight?.flightId;
}

function getAffectedFlightChanges(affectedFlight, currentFlight) {
    const newStd = normalizeTimeValue(affectedFlight?.new_std ?? affectedFlight?.newStd);
    const newSta = normalizeTimeValue(affectedFlight?.new_sta ?? affectedFlight?.newSta);
    const blockTimeMinutes = newStd && newSta ? Math.max(dayjs.utc(newSta).diff(dayjs.utc(newStd), "minute"), 0) : currentFlight?.block_time_minutes;
    const calculationNotes = normalizeCalculationNotes(affectedFlight, currentFlight);

    return {
        std: newStd,
        sta: newSta,
        flight_date: newStd ? dayjs.utc(newStd).format("YYYY-MM-DD") : currentFlight?.flight_date,
        day_of_week: newStd ? getIsoDay(newStd) : currentFlight?.day_of_week,
        block_time_minutes: blockTimeMinutes,
        change_status: currentFlight?.change_status === "ADDED" ? "ADDED" : "MODIFIED",
        change_source: "ENGINE",
        ...(calculationNotes.length ? { _calculation_notes: calculationNotes } : {}),
    };
}

function normalizeCalculationNotes(affectedFlight, currentFlight) {
    const flightId = getAutoCalculateAffectedFlightId(affectedFlight);
    const rawNotes = affectedFlight?.calculation_notes || affectedFlight?.calculationNotes;
    const calculationNotes = Array.isArray(rawNotes) ? rawNotes : [];
    const adjustments = Array.isArray(affectedFlight?.adjustments) ? affectedFlight.adjustments : [];
    const sourceNotes = calculationNotes.length
        ? calculationNotes
        : adjustments.filter((adjustment) => {
            const type = String(adjustment?.type || "");
            return type.startsWith("curfew_") || type === "aircraft_availability" || type === "aircraft_unavailable_near";
        });
    return sourceNotes.map((note) => {
        const rawType = note?.type || "";
        const normalizedType = rawType === "curfew_departure" || rawType === "curfew_arrival"
            ? "auto_curfew_delay"
            : rawType === "aircraft_availability"
                ? "auto_aircraft_availability_delay"
                : (rawType || "auto_curfew_delay");
        const isAircraftAvailabilityNote = normalizedType === "auto_aircraft_availability_delay" || normalizedType === "aircraft_unavailable_near";
        const delayMinutes = note?.delay_minutes ?? note?.delayMinutes ?? null;
        const airport = note?.airport || note?.airport_code || note?.airportCode || (note?.type === "curfew_arrival" ? currentFlight?.arrival_airport : currentFlight?.departure_airport);
        return {
            ...note,
            type: normalizedType,
            constraint_type: note?.constraint_type || (isAircraftAvailabilityNote && normalizedType !== "auto_aircraft_availability_delay" ? "H5_MAINTENANCE" : "AUTO_CALC_DELAY"),
            flight_id: note?.flight_id ?? note?.flightId ?? flightId,
            flight_number: note?.flight_number ?? note?.flightNumber ?? currentFlight?.flight_number,
            reason: note?.reason || (isAircraftAvailabilityNote ? "aircraft_availability" : "curfew"),
            airport,
            delay_minutes: delayMinutes,
            original_std: normalizeTimeValue(note?.original_std ?? note?.originalStd ?? affectedFlight?.original_std ?? affectedFlight?.originalStd),
            new_std: normalizeTimeValue(note?.new_std ?? note?.newStd ?? affectedFlight?.new_std ?? affectedFlight?.newStd),
            original_sta: normalizeTimeValue(note?.original_sta ?? note?.originalSta ?? affectedFlight?.original_sta ?? affectedFlight?.originalSta),
            new_sta: normalizeTimeValue(note?.new_sta ?? note?.newSta ?? affectedFlight?.new_sta ?? affectedFlight?.newSta),
            original_time_utc: normalizeTimeValue(note?.original_time_utc ?? note?.originalTimeUtc),
            new_time_utc: normalizeTimeValue(note?.new_time_utc ?? note?.newTimeUtc),
            time_basis: note?.time_basis || "UTC",
            source: note?.source || "auto-calculate",
            severity: note?.severity || (normalizedType === "aircraft_unavailable_near" ? "WARNING" : "info"),
            status_label: note?.status_label || (normalizedType === "aircraft_unavailable_near" ? "临近" : "顺延"),
            constraint_label: note?.constraint_label || (normalizedType === "aircraft_unavailable_near" ? "临近不可用窗口" : "自动顺延"),
            message: note?.message || `自动计算因 ${airport || (isAircraftAvailabilityNote ? "飞机" : "机场")} ${isAircraftAvailabilityNote ? "不可用窗口" : "宵禁"}顺延${delayMinutes == null ? "" : ` ${delayMinutes} 分钟`}`,
        };
    });
}

export function buildWorkingFlightsSnapshot(localFlightsMap, aircraftReg, trackId, overridesByFlightId = null) {
    return buildAircraftWorkingFlightsSnapshot(localFlightsMap, aircraftReg, trackId, overridesByFlightId);
}

function getBatchCalculationGroups(localFlightsMap, changedFlightsById) {
    const groupKeys = new Set();
    changedFlightsById.forEach((flight) => {
        groupKeys.add(`${flight.track_id || ""}::${flight.aircraft_reg || ""}`);
    });

    return Array.from(groupKeys).map((groupKey) => {
        const [trackId, aircraftReg] = groupKey.split("::");
        const chainFlights = Array.from(localFlightsMap.values())
            .filter((flight) => String(flight?.track_id || "") === trackId)
            .filter((flight) => String(flight?.aircraft_reg || "") === aircraftReg)
            .filter((flight) => String(flight?.change_status || "").toUpperCase() !== "DELETED");
        const firstChangedIndex = chainFlights.findIndex((flight) => changedFlightsById.has(String(flight.id)));
        if (firstChangedIndex < 0) {
            return null;
        }

        return {
            startFlight: chainFlights[Math.max(firstChangedIndex - 1, 0)],
            chainFlights,
        };
    }).filter(Boolean);
}

function combineBatchPreviewResponses(previews = []) {
    const responses = previews.map((preview) => preview?.response).filter(Boolean);
    const affectedFlights = responses.flatMap((response) => response?.affected_flights || response?.affectedFlights || []);
    return {
        start_flight_id: previews[0]?.startFlight?.id || null,
        stop_reason: responses.length > 1 ? `完成 ${responses.length} 条航班链` : responses[0]?.stop_reason,
        affected_flights: affectedFlights,
    };
}

export function buildFlightSnapshotChangeSet({ localFlightsMap, flights = [], type = AUTO_CALCULATE_FLIGHTS, label, fields = [] }) {
    const changes = [];
    flights.forEach((afterFlight) => {
        const flightId = afterFlight?.id ?? afterFlight?.flight_id;
        if (!flightId) {
            return;
        }

        const flightIdStr = String(flightId);
        const beforeFlight = localFlightsMap.get(flightIdStr);
        if (!beforeFlight) {
            return;
        }

        fields.forEach((field) => {
            const before = beforeFlight[field] ?? null;
            const after = afterFlight[field] ?? null;
            if (!valuesEqual(field, before, after)) {
                changes.push({
                    flightId: flightIdStr,
                    field,
                    before,
                    after,
                });
            }
        });
    });

    return {
        id: `preview-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type,
        label: label || `${type}`,
        timestamp: Date.now(),
        changes,
    };
}

export function mergeChangeSetChanges(...changeGroups) {
    const order = [];
    const changesByKey = new Map();

    changeGroups.flat().filter(Boolean).forEach((change) => {
        const flightId = change.flightId ?? change.flight_id;
        if (!flightId || !change.field) {
            return;
        }

        const key = `${String(flightId)}:${change.field}`;
        if (!changesByKey.has(key)) {
            order.push(key);
            changesByKey.set(key, {
                ...change,
                flightId: String(flightId),
            });
            return;
        }

        const existing = changesByKey.get(key);
        if (change.field === "change_source" && existing.after != null && existing.after !== "") {
            return;
        }

        changesByKey.set(key, {
            ...existing,
            after: change.after ?? null,
        });
    });

    return order
        .map((key) => changesByKey.get(key))
        .filter((change) => !valuesEqual(change.field, change.before, change.after));
}

export function buildAutoCalculateChangeSet({ response, localFlightsMap, startFlight, type = AUTO_CALCULATE_FLIGHTS, label, airportConstraintsMap = {} }) {
    const changes = [];
    const affectedFlights = response?.affected_flights || response?.affectedFlights || [];

    affectedFlights.forEach((affectedFlight) => {
        const flightId = getAutoCalculateAffectedFlightId(affectedFlight);
        if (!flightId) {
            return;
        }

        const flightIdStr = String(flightId);
        const currentFlight = localFlightsMap.get(flightIdStr);
        if (!currentFlight) {
            return;
        }

        const afterValues = getAffectedFlightChanges(affectedFlight, currentFlight);
        Object.entries(afterValues).forEach(([field, after]) => {
            const before = currentFlight[field] ?? null;
            if (!valuesEqual(field, before, after)) {
                changes.push({
                    flightId: flightIdStr,
                    field,
                    before,
                    after: after ?? null,
                });
            }
        });
    });

    return {
        id: `auto-calc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type,
        label: label || `自动计算 ${startFlight?.flight_number || startFlight?.id || "航班链"}`,
        timestamp: Date.now(),
        changes: mergeCalculatedGroundTimeChanges(changes, localFlightsMap, airportConstraintsMap),
    };
}

export async function buildPreviewFromAutoCalculate({
    draftId,
    localFlightsMap,
    startFlight,
    options,
    workingFlights,
    preChanges = [],
    type = AUTO_CALCULATE_FLIGHTS,
    label,
    airportConstraintsMap = {},
}) {
    const requestPayload = {
        start_flight_id: startFlight.id,
        track_id: startFlight.track_id,
        commit: false,
        options,
        working_flights: workingFlights || buildWorkingFlightsSnapshot(localFlightsMap, startFlight.aircraft_reg, startFlight.track_id),
    };

    const response = await scheduleDraftDataService.autoCalculateFlights(draftId, requestPayload);
    const autoChangeSet = buildAutoCalculateChangeSet({
        response,
        localFlightsMap,
        startFlight,
        type,
        label,
        airportConstraintsMap,
    });
    const mergedChanges = mergeChangeSetChanges(preChanges, autoChangeSet.changes);
    const changeSet = {
        ...autoChangeSet,
        changes: mergeCalculatedGroundTimeChanges(mergedChanges, localFlightsMap, airportConstraintsMap),
    };

    return {
        response,
        requestPayload,
        changeSet,
        startFlight,
        type,
        label: changeSet.label,
    };
}

export async function buildBatchPreviewFromChangedFlights({
    draftId,
    localFlightsMap,
    changedFlightsById,
    fields,
    type,
    label,
    options,
    beforeCalculate,
    airportConstraintsMap = {},
}) {
    const changedFlights = Array.from(changedFlightsById.values());
    const directChangeSet = buildFlightSnapshotChangeSet({
        localFlightsMap,
        flights: changedFlights,
        fields,
        type,
        label,
    });
    const groups = getBatchCalculationGroups(localFlightsMap, changedFlightsById);
    if (!groups.length) {
        return null;
    }

    beforeCalculate?.();

    const previews = [];
    for (const group of groups) {
        const workingFlights = buildWorkingFlightsSnapshot(
            localFlightsMap,
            group.startFlight.aircraft_reg,
            group.startFlight.track_id,
            changedFlightsById,
        );
        previews.push(await buildPreviewFromAutoCalculate({
            draftId,
            localFlightsMap,
            startFlight: group.startFlight,
            options,
            workingFlights,
            type,
            label,
            airportConstraintsMap,
        }));
    }

    const autoChanges = previews.flatMap((preview) => preview.changeSet?.changes || []);
    const mergedChanges = mergeChangeSetChanges(directChangeSet.changes, autoChanges);
    const changeSet = {
        ...directChangeSet,
        changes: mergeCalculatedGroundTimeChanges(mergedChanges, localFlightsMap, airportConstraintsMap),
    };

    return {
        ...previews[0],
        response: combineBatchPreviewResponses(previews),
        requestPayload: {
            requests: previews.map((preview) => preview.requestPayload),
        },
        changeSet,
        type,
        label,
    };
}

export function formatAutoCalculatePreviewTime(value) {
    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed.format("MM-DD HH:mm") : "--";
}

export function getAutoCalculateStopReasonLabel(stopReason) {
    switch (stopReason) {
        case "caught_up":
            return "最小影响模式已追平原计划";
        case "discontinuity":
            return "飞机链路连续性中断";
        case "end_of_chain":
            return "已计算至链路末尾";
        default:
            return stopReason || "计算完成";
    }
}
